/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * SearchInput — Premium Search Bar with Focus Glow & Clear Button
 * ============================================================================
 *
 * Drop-in replacement for plain search inputs across all list pages.
 * Features: brand accent focus ring, animated search icon, clear pill,
 * optional result count badge, CoreX design system variables.
 *
 * ============================================================================
 */

import React, { useRef } from 'react';
import { Search, X } from 'lucide-react';

export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** Show result count badge when searching */
  resultCount?: number;
  /** Auto-focus on mount */
  autoFocus?: boolean;
}

export const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  placeholder = 'Search...',
  className = '',
  resultCount,
  autoFocus = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <style>{searchInputStyles}</style>
      <div className={`si-root ${value ? 'si-has-value' : ''} ${className}`}>
        <Search size={15} className="si-icon" />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="si-input"
          autoFocus={autoFocus}
        />
        {value && (
          <button
            type="button"
            className="si-clear"
            onClick={() => {
              onChange('');
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
          >
            <X size={12} />
          </button>
        )}
        {value && resultCount !== undefined && (
          <span className="si-count">{resultCount}</span>
        )}
      </div>
    </>
  );
};

const searchInputStyles = `
  .si-root {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    height: 40px;
    padding: 0 0.75rem;
    background: var(--color-bg-secondary);
    border: 1.5px solid var(--color-border-subtle);
    border-radius: var(--radius-md, 6px);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    overflow: hidden;
  }
  .si-root::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    height: 2px;
    width: 0;
    background: var(--color-brand-primary);
    transition: width 0.3s cubic-bezier(0.16,1,0.3,1);
    border-radius: 0 0 var(--radius-md, 6px) var(--radius-md, 6px);
  }
  .si-root:focus-within {
    border-color: var(--color-brand-primary);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-brand-primary) 12%, transparent);
  }
  .si-root:focus-within::after {
    width: 100%;
  }
  .si-icon {
    color: var(--color-text-muted);
    flex-shrink: 0;
    transition: color 0.2s ease, transform 0.2s ease;
  }
  .si-root:focus-within .si-icon {
    color: var(--color-brand-primary);
    transform: scale(1.08);
  }
  .si-input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.84rem;
    color: var(--color-text-primary);
    height: 100%;
  }
  .si-input::placeholder {
    color: var(--color-text-muted);
  }
  .si-clear {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: none;
    border-radius: var(--radius-sm, 4px);
    background: var(--color-bg-tertiary);
    color: var(--color-text-muted);
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s ease;
    animation: si-clear-in 0.15s cubic-bezier(0.34,1.56,0.64,1);
  }
  @keyframes si-clear-in {
    from { transform: scale(0); opacity: 0; }
    to   { transform: scale(1); opacity: 1; }
  }
  .si-clear:hover {
    background: color-mix(in srgb, var(--color-error) 15%, var(--color-bg-tertiary));
    color: var(--color-error);
    transform: scale(1.1);
  }
  .si-count {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 0.65rem;
    font-weight: 700;
    color: var(--color-brand-primary);
    background: color-mix(in srgb, var(--color-brand-primary) 10%, var(--color-bg-secondary));
    padding: 1px 6px;
    border-radius: 9999px;
    flex-shrink: 0;
    animation: si-clear-in 0.15s cubic-bezier(0.34,1.56,0.64,1);
  }

  @media (prefers-reduced-motion: reduce) {
    .si-root, .si-root::after, .si-icon, .si-clear, .si-count {
      animation: none !important;
      transition-duration: 0ms !important;
    }
  }
`;

export default SearchInput;
